import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Grid2 as Grid,
  IconButton,
  TextField,
  styled
} from '@mui/material'
import ButtonBase from 'design-system/button'
import AccordionForm, {
  AccordionFormProps,
  StyledAccordionBase
} from './components/AccordionForm'
import SelectBox from 'components/SelectBox'
import MedicalInfoSkeleton from './skeleton'
import dayjs from 'dayjs'
import {
  Delete as DeleteIcon,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined
} from '@mui/icons-material'
import { useShowBannerHDA } from 'utils/hda'
import { GridColDef } from '@mui/x-data-grid'
import { IPatientSurgeriesRequest } from 'types/patient-surgeries/type'
import SurgeryDate from './components/SurgeryDate'
import useMedicalInfo from './useMedicalInfo'
import DataGridBase from 'design-system/DataGrid'
import {
  DEFAULT_CONTAINER_PROPS,
  FULL_WIDTH_CONTAINER_PROPS,
  PAPER_FIELD_SX,
  makeDateField,
  makeNumberField,
  makeSwitchField
} from './fieldFactories'

type SurgeryGridRow = IPatientSurgeriesRequest & {
  _rowId: string
  _index: number
}

const toSafeList = <T,>(prev: T[] | null | undefined): T[] =>
  Array.isArray(prev) ? [...prev] : []

const StyledSurgeriesDataGrid = styled(DataGridBase<SurgeryGridRow>)(
  ({ theme }) => ({
    '& .MuiDataGrid-topContainer': {
      '& [role="row"]': {
        backgroundColor: theme.palette.primarySoft.main
      },
      '&, & svg': {
        color: theme.palette.primarySoft.contrastText
      }
    },
    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center'
    },
    '& .MuiDataGrid-cell .MuiFormControl-root': {
      width: '100%'
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
      outline: 'none'
    }
  })
)

/**
 * Medical Form Page
 * @returns JSX.Element
 */
const MedicalFormPage: React.FC = () => {
  const {
    expandedList,
    setExpandedList,
    surgeries,
    getFieldControl,
    getFieldValue,
    setFieldValue,
    saveData,
    state,
    isEdited,
    hasUnsavedSurgeriesChanges,
    isMobile,
    updateSurgeries
  } = useMedicalInfo()

  const isHda = useShowBannerHDA()
  const hiddenItem = isHda
  const offset = hiddenItem ? 1 : 0
  const [showIncompleteSurgeriesError, setShowIncompleteSurgeriesError] =
    useState(false)
  const surgeriesSectionRef = useRef<HTMLDivElement | null>(null)

  const canAddSurgery = useMemo(() => {
    if (!Array.isArray(surgeries) || surgeries.length === 0) {
      return true
    }

    return surgeries.every(surgery => {
      const surgeryType =
        typeof surgery?.surgery_type === 'string' ? surgery.surgery_type : ''
      const hasSurgeryType = surgeryType.trim().length > 0

      const surgeryDate = surgery?.surgery_date
      const hasSurgeryDate =
        surgeryDate != null &&
        (dayjs.isDayjs(surgeryDate)
          ? surgeryDate.isValid()
          : dayjs(surgeryDate).isValid())

      return hasSurgeryType && hasSurgeryDate
    })
  }, [surgeries])

  const hasIncompleteSurgeries =
    Array.isArray(surgeries) && surgeries.length > 0 && !canAddSurgery

  useEffect(() => {
    if (!hasIncompleteSurgeries) {
      setShowIncompleteSurgeriesError(false)
    }
  }, [hasIncompleteSurgeries])

  const updateSurgeryField = useCallback(
    (index: number, patch: Partial<IPatientSurgeriesRequest>) => {
      updateSurgeries(prev => {
        const safeList = toSafeList(prev)
        if (!safeList[index]) {
          safeList[index] = {
            surgery_date: null,
            surgery_type: ''
          }
        }

        safeList[index] = {
          ...safeList[index],
          ...patch
        }

        return safeList
      })
    },
    [updateSurgeries]
  )

  const handleAddSurgery = () => {
    if (!canAddSurgery) {
      return
    }

    const newSurgery: IPatientSurgeriesRequest = {
      surgery_date: null,
      surgery_type: ''
    }

    updateSurgeries(prev => {
      const safeList = toSafeList(prev)
      safeList.push(newSurgery)
      return safeList
    })
  }

  const handleRemoveSurgery = useCallback(
    (index: number) => {
      updateSurgeries(prev => {
        const safeList = toSafeList(prev)
        safeList.splice(index, 1)
        return safeList
      })
    },
    [updateSurgeries]
  )

  const surgeryRows = useMemo<SurgeryGridRow[]>(() => {
    if (!Array.isArray(surgeries)) {
      return []
    }

    return surgeries.map((surgery, index) => ({
      ...surgery,
      _rowId: surgery.surgery_id || `surgery-${index}`,
      _index: index
    }))
  }, [surgeries])

  const surgeryColumns = useMemo<GridColDef<SurgeryGridRow>[]>(
    () => [
      {
        field: 'surgery_date',
        headerName: 'Data e hora da cirurgia',
        sortable: false,
        filterable: false,
        flex: 1,
        minWidth: 260,
        renderCell: params => (
          <SurgeryDate
            value={params.row.surgery_date}
            showLabel={false}
            size='small'
            onChange={val =>
              updateSurgeryField(params.row._index, { surgery_date: val })
            }
          />
        )
      },
      {
        field: 'surgery_type',
        headerName: 'Tipo de cirurgia',
        sortable: false,
        filterable: false,
        flex: 1,
        minWidth: 240,
        renderCell: params => (
          <TextField
            value={params.row.surgery_type ?? ''}
            size='small'
            placeholder='Tipo de cirurgia'
            onKeyDown={event => event.stopPropagation()}
            onChange={event =>
              updateSurgeryField(params.row._index, {
                surgery_type: event.target.value
              })
            }
            sx={PAPER_FIELD_SX}
          />
        )
      },
      {
        field: 'actions',
        headerName: 'Ações',
        sortable: false,
        filterable: false,
        width: 100,
        renderCell: params => (
          <IconButton
            aria-label='Remover cirurgia'
            onClick={() => handleRemoveSurgery(params.row._index)}
          >
            <DeleteIcon />
          </IconButton>
        )
      }
    ],
    [updateSurgeryField, handleRemoveSurgery]
  )

  const changeVisibility = (expanded: boolean, index: number) => {
    const newList = [...expandedList]
    newList[index] = expanded
    setExpandedList(newList)
  }

  const formFields: Array<AccordionFormProps> = [
    {
      id: 'patient-milestones',
      title: 'Etapas do estudo',
      hidden: !isHda,
      fields: [
        makeDateField({
          label: 'Instalação',
          fieldKey: 'installation_date',
          id: 'installation_date',
          getFieldValue,
          setFieldValue
        }),
        makeDateField({
          label: 'Calibração',
          fieldKey: 'calibration_date',
          id: 'calibration_date',
          getFieldValue,
          setFieldValue
        }),
        makeDateField({
          label: 'Retorno',
          fieldKey: 'return_date',
          id: 'return_date',
          getFieldValue,
          setFieldValue
        }),
        {
          type: 'text',
          fieldProps: {
            label: 'Descrição',
            value: getFieldValue('description') ?? '',
            onChange: e => setFieldValue('description', e.target.value),
            multiline: true,
            rows: 4,
            sx: PAPER_FIELD_SX
          },
          containerProps: FULL_WIDTH_CONTAINER_PROPS,
          id: 'description'
        }
      ]
    },
    {
      id: 'basic-info',
      title: 'Informações básicas',
      fields: [
        makeDateField({
          label: 'Data de nascimento',
          fieldKey: 'birth_date',
          id: 'birth_date',
          getFieldValue,
          setFieldValue
        }),
        {
          type: 'select',
          fieldProps: {
            label: 'Sexo',
            menuItems: [
              {
                value: 'true',
                label: 'Feminino'
              },
              {
                value: 'false',
                label: 'Masculino'
              }
            ],
            control: getFieldControl('birth_sex')
          },
          containerProps: DEFAULT_CONTAINER_PROPS,
          id: 'gender'
        },
        makeNumberField({
          label: 'Altura',
          fieldKey: 'height_cm',
          id: 'heigth',
          unit: 'cm',
          mask: String.raw`\d+`,
          getFieldControl
        }),
        makeNumberField({
          label: 'Peso',
          fieldKey: 'weight_kg',
          id: 'weigth',
          unit: 'kg',
          mask: String.raw`\d+(\.)?(\d+)?`,
          getFieldControl
        })
      ]
    },
    {
      id: 'family-history',
      title: 'Histórico prévio e familiar',
      fields: [
        {
          children: (
            <SelectBox
              label='Hipertensão na família'
              menuItems={[
                {
                  value: '0',
                  label: 'Sem histórico'
                },
                {
                  value: '1',
                  label: 'Pai ou mãe'
                },
                {
                  value: '2',
                  label: 'Pai e mãe'
                }
              ]}
              control={getFieldControl('has_parental_hypertension')}
            />
          ),
          containerProps: DEFAULT_CONTAINER_PROPS,
          id: 'hypertension-history'
        },
        makeSwitchField({
          title: 'Doença cardiovascular prévia?',
          fieldKey: 'has_cvd',
          id: 'cvd',
          getFieldControl
        })
      ]
    },
    {
      id: 'cardiovascular-info',
      title: 'Informações cardiovasculares',
      hidden: hiddenItem,
      fields: [
        makeNumberField({
          label: 'Intervalo de PR',
          fieldKey: 'pr_interval',
          id: 'pr-interval',
          unit: 'ms',
          mask: String.raw`\d+`,
          getFieldControl
        }),
        makeNumberField({
          label: 'Fração de Ejeção',
          fieldKey: 'ejection_fraction',
          id: 'ejection-fraction',
          unit: '%',
          mask: String.raw`\d+`,
          getFieldControl
        })
      ]
    },
    {
      id: 'Habits-medications',
      title: 'Hábitos e medicações',
      fields: [
        makeSwitchField({
          title: 'Fumante?',
          fieldKey: 'is_smoker',
          id: 'is-smoker',
          getFieldControl
        }),
        makeSwitchField({
          title: 'Faz uso de oxigênio?',
          fieldKey: 'use_oxygen',
          id: 'use-oxygen',
          getFieldControl
        }),
        makeSwitchField({
          title: 'Usa medicação para hipertensão?',
          fieldKey: 'has_hypertension_treatment',
          id: 'hypertension-medication',
          getFieldControl
        })
      ]
    },
    {
      id: 'chronic-diseases',
      title: 'Doenças crônicas',
      fields: [
        makeSwitchField({
          title: 'Diabetes?',
          fieldKey: 'has_diabetes',
          id: 'diabetes',
          getFieldControl
        }),
        makeSwitchField({
          title: 'Hipertensão?',
          fieldKey: 'hypertension',
          id: 'hypertension',
          getFieldControl
        }),
        makeSwitchField({
          title: 'Hipertrofia ventricular esquerda?',
          fieldKey: 'has_left_ventricular_hypertrophy',
          id: 'LVH',
          hidden: hiddenItem,
          getFieldControl
        }),
        makeSwitchField({
          title: 'Fibrilação atrial?',
          fieldKey: 'has_atrial_fibrillation',
          id: 'atrial-fibrillation',
          hidden: hiddenItem,
          getFieldControl
        }),
        makeSwitchField({
          title: 'Sopro cardíaco?',
          fieldKey: 'has_murmur',
          id: 'heart-murmur',
          hidden: hiddenItem,
          getFieldControl
        }),
        makeSwitchField({
          title: 'Insuficiência cardíaca?',
          fieldKey: 'heart_failure',
          id: 'heart-failure',
          getFieldControl
        }),
        makeSwitchField({
          title: 'Cardiopatia?',
          fieldKey: 'cardiopathy',
          id: 'cardiopathy',
          getFieldControl
        }),
        makeSwitchField({
          title: 'Ansiedade/depressão?',
          fieldKey: 'depression',
          id: 'depression',
          getFieldControl
        }),
        makeSwitchField({
          title: 'Doenças respiratórias?',
          fieldKey: 'respiratory_diseases',
          id: 'respiratory-diseases',
          getFieldControl
        })
      ]
    }
  ]

  const changeGlobalVisibility = () => {
    const newValue = [...expandedList]

    const action: boolean = expandedList.includes(false)

    const totalAccordions = formFields.length + offset
    for (let index = 0; index < totalAccordions; index += 1) {
      newValue[index] = action
    }

    setExpandedList(newValue)
  }

  const handleSave = () => {
    if (hasIncompleteSurgeries) {
      setShowIncompleteSurgeriesError(true)

      if (hiddenItem) {
        setExpandedList(prev => {
          const next = [...prev]
          next[0] = true
          return next
        })
      }

      requestAnimationFrame(() => {
        surgeriesSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      })

      return
    }

    setShowIncompleteSurgeriesError(false)
    saveData()
  }

  if (state == 'idle') {
    return <MedicalInfoSkeleton />
  }

  return (
    <Box sx={{ padding: 2, width: '100%' }}>
      <Grid container direction='column' spacing={2}>
        <Box
          sx={{
            alignSelf: 'end'
          }}
        >
          <ButtonBase
            variant='outlined'
            sx={{
              background: 'none',
              minWidth: '100px',
              color: 'primarySoft.main',
              borderColor: 'primarySoft.main'
            }}
            onClick={changeGlobalVisibility}
          >
            {expandedList.length == 0 || !expandedList.includes(false) ? (
              <>
                <KeyboardArrowUpOutlined /> Fechar fichas
              </>
            ) : (
              <>
                <KeyboardArrowDownOutlined /> Abrir fichas
              </>
            )}
          </ButtonBase>
        </Box>
        {hiddenItem && (
          <Box ref={surgeriesSectionRef}>
            <StyledAccordionBase
              title='Dados da cirurgia'
              onChange={(_, expanded) => changeVisibility(expanded, 0)}
              expanded={expandedList[0] ?? true}
            >
              <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                <ButtonBase
                  variant='outlined'
                  onClick={handleAddSurgery}
                  disabled={!canAddSurgery}
                  sx={{
                    color: 'primarySoft.main',
                    borderColor: 'primarySoft.main'
                  }}
                >
                  Adicionar cirurgia
                </ButtonBase>
              </Box>
              <StyledSurgeriesDataGrid
                rows={surgeryRows}
                columns={surgeryColumns}
                loading={state == 'loading'}
                getRowId={row => row._rowId}
                rowHeight={64}
                disableRowSelectionOnClick
                disableColumnMenu
                hideFooter
              />
              {hasUnsavedSurgeriesChanges && (
                <Alert
                  severity='warning'
                  variant='outlined'
                  color='warning'
                  sx={{ mt: 2 }}
                >
                  Existem alterações nas cirurgias que ainda não foram salvas.
                  Clique em Salvar no final do formulário para confirmar.
                </Alert>
              )}
              {showIncompleteSurgeriesError && (
                <Alert severity='error' variant='outlined' sx={{ mt: 2 }}>
                  Preencha data e tipo em todas as cirurgias antes de salvar o
                  formulário.
                </Alert>
              )}
            </StyledAccordionBase>
          </Box>
        )}
        {formFields.map((accordionForm, index) => (
          <AccordionForm
            key={`medical-info-${accordionForm.id}`}
            {...accordionForm}
            accordionProps={{
              onChange: (_, expanded) =>
                changeVisibility(expanded, index + offset),
              expanded: expandedList[index + offset] ?? true
            }}
          />
        ))}
        <Grid
          spacing={1}
          justifyContent={isMobile ? 'center' : 'start'}
          container
        >
          <ButtonBase
            variant='contained'
            type='submit'
            loading={state == 'loading'}
            disabled={!isEdited || state == 'loading'}
            onClick={handleSave}
            sx={{ minWidth: '200px' }}
          >
            Salvar
          </ButtonBase>
        </Grid>
      </Grid>
    </Box>
  )
}

export default MedicalFormPage
